import React, { useMemo, useState } from "react";
import "../styles/ChangePasswordPage.css";
import { useI18n } from "../providers/I18nProvider";
import { apiClient } from "../services/apiClient";
import useUnsavedChanges from "../hooks/useUnsavedChanges";

const MIN_LENGTH = 6; // backend (changePasswordValidators) ile aynı

// 0-4 arası basit güç skoru: uzunluk + karakter çeşitliliği. Yalnızca yol gösterir, zorunlu değildir.
const scorePassword = (pw) => {
  if (!pw) return 0;
  let score = 0;
  if (pw.length >= MIN_LENGTH) score += 1;
  if (pw.length >= 10) score += 1;
  if (/[a-zçğıöşü]/.test(pw) && /[A-ZÇĞİÖŞÜ]/.test(pw)) score += 1;
  if (/\d/.test(pw) && /[^A-Za-z0-9çğıöşüÇĞİÖŞÜ]/.test(pw)) score += 1;
  return Math.min(score, 4);
};

const ChangePasswordPage = () => {
  const { t } = useI18n();
  const [currentPassword, setCurrentPassword] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [newPassword2, setNewPassword2] = useState("");
  const [showPw, setShowPw] = useState(false);
  const [saving, setSaving] = useState(false);
  const [pwMsg, setPwMsg] = useState({ text: "", tone: "muted" });

  const dirty = Boolean(currentPassword || newPassword || newPassword2);
  useUnsavedChanges(dirty);

  const score = useMemo(() => scorePassword(newPassword), [newPassword]);
  const tooShort = newPassword.length > 0 && newPassword.length < MIN_LENGTH;
  const mismatch = newPassword2.length > 0 && newPassword !== newPassword2;
  const sameAsOld = newPassword.length > 0 && newPassword === currentPassword;

  const changePassword = async (e) => {
    e?.preventDefault?.();
    if (saving) return;
    setPwMsg({ text: "", tone: "muted" });
    if (!currentPassword || !newPassword) {
      setPwMsg({ text: t("account.password.required"), tone: "err" });
      return;
    }
    if (tooShort) {
      setPwMsg({ text: t("changePassword.tooShort", { min: MIN_LENGTH }), tone: "err" });
      return;
    }
    if (newPassword !== newPassword2) {
      setPwMsg({ text: t("account.password.mismatch"), tone: "err" });
      return;
    }
    if (sameAsOld) {
      setPwMsg({ text: t("changePassword.sameAsOld"), tone: "err" });
      return;
    }
    setSaving(true);
    try {
      await apiClient.put("/users/me/password", {
        current_password: currentPassword,
        new_password: newPassword,
      });
      setCurrentPassword("");
      setNewPassword("");
      setNewPassword2("");
      setShowPw(false);
      setPwMsg({ text: t("account.password.success"), tone: "ok" });
    } catch (err) {
      setPwMsg({
        text: err.response?.data?.msg || err.message || t("common.genericError"),
        tone: "err",
      });
    } finally {
      setSaving(false);
    }
  };

  const type = showPw ? "text" : "password";

  return (
    <div className="change-password-page">
      <div className="card">
        <div className="card-head">
          <h2>{t("account.password.title")}</h2>
          <div className="muted small">••••••</div>
        </div>
        <form className="form" onSubmit={changePassword} noValidate>
          <div className="form-row">
            <label>
              {t("account.password.current")}
              <input
                type={type}
                autoComplete="current-password"
                value={currentPassword}
                onChange={(e) => setCurrentPassword(e.target.value)}
              />
            </label>
          </div>
          <div className="form-row split">
            <label>
              {t("account.password.new")}
              <input
                type={type}
                autoComplete="new-password"
                value={newPassword}
                aria-invalid={tooShort || sameAsOld}
                onChange={(e) => setNewPassword(e.target.value)}
              />
              {newPassword && (
                <div className="pw-meter" aria-live="polite">
                  <div className="pw-meter-bar">
                    {[1, 2, 3, 4].map((i) => (
                      <span key={i} className={i <= score ? `on s${score}` : ""} />
                    ))}
                  </div>
                  <span className="muted small">{t(`changePassword.strength.${score}`)}</span>
                </div>
              )}
              {tooShort && (
                <span className="field-error" role="alert">
                  {t("changePassword.tooShort", { min: MIN_LENGTH })}
                </span>
              )}
              {sameAsOld && (
                <span className="field-error" role="alert">
                  {t("changePassword.sameAsOld")}
                </span>
              )}
            </label>
            <label>
              {t("account.password.new2")}
              <input
                type={type}
                autoComplete="new-password"
                value={newPassword2}
                aria-invalid={mismatch}
                onChange={(e) => setNewPassword2(e.target.value)}
              />
              {mismatch && (
                <span className="field-error" role="alert">
                  {t("account.password.mismatch")}
                </span>
              )}
            </label>
          </div>
          <label className="show-pw">
            <input type="checkbox" checked={showPw} onChange={(e) => setShowPw(e.target.checked)} />
            {t("changePassword.show")}
          </label>
          {pwMsg.text && (
            <div className={`msg ${pwMsg.tone}`} role={pwMsg.tone === "err" ? "alert" : "status"}>
              {pwMsg.text}
            </div>
          )}
          <div className="actions">
            <button className="primary" type="submit" disabled={saving}>
              {saving ? t("changePassword.saving") : t("account.password.save")}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default ChangePasswordPage;
